import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PhoneCall, Plus, Clock, Repeat, Phone, CheckCircle2, AlertCircle, MinusCircle, Users, Check } from "lucide-react";

const STATUS_ICON = { sent: CheckCircle2, failed: AlertCircle, simulated: MinusCircle, requires_premium: AlertCircle };
const STATUS_COLOR = { sent: "#2A4B46", failed: "#DC2626", simulated: "#5c5c59", requires_premium: "#D36B46" };

export default function CheckInPanel({ reload }) {
  const [roster, setRoster] = useState([]);
  const [log, setLog] = useState({ date: "", items: [] });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({ name: "", phone: "", time: "08:00", voice_script: "" });
  const [busy, setBusy] = useState(false);

  const loadRoster = () => api.get("/checkins/roster").then((r) => setRoster(r.data)).catch(() => {});
  const loadLog = (d) => api.get("/checkins/log", { params: { on_date: d } }).then((r) => setLog(r.data)).catch(() => {});
  const toggleLogDone = async (dp) => { try { await api.put(`/dispatches/${dp.id}/toggle`); loadLog(date); } catch (e) {} };

  useEffect(() => { loadRoster(); }, []);
  useEffect(() => { loadLog(date); }, [date]);

  const add = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Name and phone are required");
    setBusy(true);
    try {
      await api.post("/checkins/quick", form);
      toast.success("Check-in client added");
      setForm({ name: "", phone: "", time: "08:00", voice_script: "" });
      loadRoster();
      if (reload) reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="checkin-panel">
      {/* Roster */}
      <section className="lg:col-span-7 border border-[var(--line)] bg-white">
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
          <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-[var(--accent)]" /> Check-In Roster
          </h2>
          <span className="label-mono text-[var(--text-2)]">{roster.length} on roster</span>
        </div>
        <div className="divide-y divide-[var(--line)]">
          {roster.length === 0 && <p className="p-8 text-sm text-[var(--text-2)] text-center">No check-in clients yet. Add one on the right.</p>}
          {roster.map((l) => (
            <div key={l.id} data-testid="checkin-roster-row" className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-[var(--accent)]/10 flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-[var(--accent)]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{l.client_name}</p>
                <div className="flex items-center gap-3 mt-1 text-[var(--text-2)] font-mono text-xs">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {l.time}</span>
                  <span className="flex items-center gap-1"><Repeat className="w-3 h-3" /> Daily</span>
                  <span className="truncate">{l.client_phone}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick add */}
      <section className="lg:col-span-5 space-y-6">
        <div className="border border-[var(--line)] bg-[#2A4B46] text-[#F9F9F7] p-6">
          <h3 className="font-serif text-xl tracking-tight flex items-center gap-2"><Users className="w-5 h-5 text-[#E37B5B]" /> Add Check-In Client</h3>
          <div className="space-y-3 mt-4">
            <input data-testid="checkin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client name" className="w-full h-11 px-3 bg-white/95 text-[#1A1A18] outline-none" />
            <input data-testid="checkin-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 305 555 0100" className="w-full h-11 px-3 bg-white/95 text-[#1A1A18] outline-none" />
            <div className="flex items-center gap-2">
              <span className="label-mono text-[#A3A39F]">Call at</span>
              <input data-testid="checkin-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-11 px-3 bg-white/95 text-[#1A1A18] outline-none" />
            </div>
            <textarea data-testid="checkin-script" value={form.voice_script} onChange={(e) => setForm({ ...form, voice_script: e.target.value })} rows={3} placeholder="Optional custom script (a friendly default is used if blank)" className="w-full px-3 py-2 bg-white/95 text-[#1A1A18] outline-none" />
            <button data-testid="checkin-add" onClick={add} disabled={busy} className="w-full h-11 bg-[#E37B5B] text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50">
              <Plus className="w-4 h-4" /> {busy ? "Adding…" : "Add daily check-in"}
            </button>
          </div>
        </div>
      </section>

      {/* Daily call log */}
      <section className="lg:col-span-12 border border-[var(--line)] bg-white">
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
          <h2 className="font-serif text-2xl tracking-tight">Daily Call Log</h2>
          <input data-testid="checkin-log-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 px-2 text-sm border border-[var(--line)] bg-white" />
        </div>
        <div className="divide-y divide-[var(--line)]">
          {log.items.length === 0 && <p className="p-8 text-sm text-[var(--text-2)] text-center">No check-in calls logged for this date yet.</p>}
          {log.items.map((d) => {
            const Icon = STATUS_ICON[d.status] || MinusCircle;
            const color = STATUS_COLOR[d.status] || "#5c5c59";
            return (
              <div key={d.id} data-testid="checkin-log-row" className="p-4 flex items-center gap-3">
                <button data-testid="checkin-done" onClick={() => toggleLogDone(d)} className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 transition-colors ${d.done ? "bg-[#2A4B46] border-[#2A4B46]" : "border-[var(--text-2)] hover:border-[#2A4B46]"}`}>{d.done && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}</button>
                <Icon className="w-5 h-5 shrink-0" style={{ color }} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${d.done ? "line-through text-[var(--text-2)]" : ""}`}>{d.title} · {d.to}</p>
                  <p className="text-xs text-[var(--text-2)] truncate">{d.message}</p>
                </div>
                <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 shrink-0" style={{ color, background: color + "1a" }}>{d.status}</span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
